// SearchBar.js
import { useState, useMemo, useEffect, forwardRef, useRef, useTransition } from 'react';
import debounce from 'lodash.debounce';
import '../assets/css/SearchBar.css';
import { getData } from '../API/GlobalApi';



const SearchingBar = forwardRef(
  (
    {
      placeholder = 'Search...',
      filterQueryString = '',
      url = '',
      setResults,
      setTotalCount,
      setFilterQueryString,
      setSearchLoading,
      setCurrentPage,
      startDate,     // ✅ add this
      endDate,       // ✅ add this
      setIsSearching,
      setIsSearchMode,
      setSearchValue,
      onSearchClear,
      searchValue,
      showToggle = false, // ✅ whether to show the toggle SVG
      toggleOnIcon,
      toggleOffIcon,
      onTransitionChange,
      transationsSearch,

    }, ref

  ) => {

    const [isPending, startTransition] = useTransition();
    useEffect(() => {
      onTransitionChange?.(isPending);
    }, [isPending]);
    const [isEnabled, setIsEnabled] = useState(true);

    const handleToggle = () => {
      setIsEnabled((prev) => !prev);

      // Optional: clear search when disabling
      if (isEnabled) {
        setSearchQuery("");
        setSearchValue?.("");
        setFilterQueryString?.((prev) => {
          const obj = typeof prev === "object" && prev !== null ? { ...prev } : {};
          const { search, ...rest } = obj;
          return { ...rest };
        });
        setCurrentPage?.(0);
        onSearchClear?.();
      }
    };

    const didPrefillSearchRef = useRef(false);

    useEffect(() => {
      if (
        typeof searchValue === "string" &&
        searchValue.trim() &&
        searchValue !== searchQuery &&
        !didPrefillSearchRef.current
      ) {
        didPrefillSearchRef.current = true;

        setSearchQuery(searchValue);

        const queryParams = buildQueryParams(filterQueryString);
        queryParams.set("search", searchValue);
        queryParams.set("page", 1);

        handleSearch(searchValue, queryParams);
      }
    }, [searchValue]);


    const buildQueryParams = (filterQuery) => {
      const queryParams = new URLSearchParams();
      if (!filterQuery) return queryParams;
      if (typeof filterQuery === "object" && !Array.isArray(filterQuery)) {
        Object.entries(filterQuery).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            queryParams.set(key, value);
          }
        });
      } else if (typeof filterQuery === "string") {
        const existingParams = new URLSearchParams(filterQuery);
        for (const [key, value] of existingParams.entries()) {
          queryParams.set(key, value);
        }
      }
      return queryParams;
    };

    const localRef = useRef(null);
    const inputRef = ref || localRef;
    useEffect(() => {
      inputRef.current?.focus(); // auto-focus on mount
    }, []);


    const blockNextEmptySearchRef = useRef(false);
    const [searchQuery, setSearchQuery] = useState('');

    const handleSearch = async (query, latestQueryParams = null) => {
      const trimmedQuery = query.trim();
      setIsSearching?.(!!trimmedQuery);
      setIsSearchMode?.(!!trimmedQuery);

      if (typeof setSearchValue === "function") {
        setSearchValue(trimmedQuery);
      }

      const queryParams = latestQueryParams || buildQueryParams(filterQueryString);

      // Remove old search
      queryParams.delete("search");

      if (trimmedQuery) {
        const words = trimmedQuery.split(/\s+/);
        const finalSearch = words.length >= 3 ? words.join("+") : trimmedQuery;
        queryParams.set("search", finalSearch);
        setCurrentPage?.(0);
      } else {
        setCurrentPage?.(0);
      }

      queryParams.set("page", 1);

      const finalQueryString = queryParams.toString().replace(/%2B/g, "+");
      const finalUrl = `${url}?${finalQueryString}`;

      console.log("this url triggers in searching", finalUrl);

      try {
        setSearchLoading?.(true);
        const response = await getData(finalUrl);

        let data = [];
        let total = 0;

        if (Array.isArray(response)) data = response;
        else if (response?.results) {
          data = response.results;
          total = response.count || response.results.length;
        } else if (response?.students?.results) {
          data = response.students.results;
          total = response.students.count || response.students.results.length;
        }
        setResults?.(data);
        setTotalCount?.(total);
        // Update state
        // setFilterQueryString?.(Object.fromEntries(queryParams.entries()));
        console.log("trimmedQuery",trimmedQuery)
         setFilterQueryString?.({
          ...Object.fromEntries(queryParams.entries()),
          search: trimmedQuery, // 👈 plain string with spaces
        });
        setCurrentPage?.(0);
      } catch (err) {
        setResults?.([]);
        setTotalCount?.(0);
      } finally {
        setSearchLoading?.(false);
      }
    };


    const debouncedSearch = useMemo(
      () => debounce(handleSearch, 500),
      [url]
    );

    const handleInputChange = (e) => {
      if (!isEnabled) return; // ignore typing if disabled
      const value = e.target.value;
      setSearchQuery(value);
      if (setSearchValue) setSearchValue(value);

      if (value.trim() === "") {
        blockNextEmptySearchRef.current = true;
        didPrefillSearchRef.current = false;
        debouncedSearch.cancel();
        setIsSearching?.(false);
        setIsSearchMode?.(false);
        setSearchValue?.("");

        // ✅ Correctly remove only the "search" key from the OBJECT
        // Remove only "search" key and keep the rest intact
        startTransition(() => {
          setFilterQueryString?.((prev) => {
            let obj = {};
            if (typeof prev === "object" && prev !== null) {
              obj = { ...prev };
            } else if (typeof prev === "string") {
              obj = Object.fromEntries(new URLSearchParams(prev).entries());
            }
            const { search, ...rest } = obj;
            return rest;
          });

          setCurrentPage?.(0);
        });
        // 🔥 notify parent that search was manually cleared
        onSearchClear?.();
        return;
      }
      const queryParams = buildQueryParams(filterQueryString);
      // startTransition(() => {
      queryParams.set("search", value);
      queryParams.set("page", 1);
      debouncedSearch(value, queryParams);
      // });

    };


    useEffect(() => {
      return () => debouncedSearch.cancel();
    }, [debouncedSearch]);

    return (
      <div className={`search-bar ${transationsSearch ? "transactions-search-bar" : ""}`} style={{ position: "relative", width: "100%" }}>
        <input
          type="text"
          className="search-input"
          placeholder={placeholder}
          value={searchQuery}
          onChange={handleInputChange}
          disabled={!isEnabled} // <-- disable if not enabled
          ref={inputRef}
          style={{
            width: "100%",
            paddingRight: "50px", // space for the icons
            outlineColor: "#7F56DA",
            outlineWidth: "1px",
            outlineStyle: "solid",
          }}
        />

        {/* Search icon (left/right inside input) */}
        {!showToggle && <svg
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          width="23"
          height="23"
          style={{
            position: "absolute",
            right: "26px",
            top: "50%",
            transform: "translateY(-50%)",
            pointerEvents: "none", // prevents blocking input typing
            stroke: "#000000",
          }}
        >
          <path
            d="M15.7955 15.8111L21 21M18 10.5C18 14.6421 14.6421 18 10.5 18C6.35786 18 3 14.6421 3 10.5C3 6.35786 6.35786 3 10.5 3C14.6421 3 18 6.35786 18 10.5Z"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>}

        {/* Toggle icon */}
        {showToggle && (
          <div
            onClick={handleToggle}
            style={{
              position: "absolute",
              right: "2px",
              top: "50%",
              transform: "translateY(-50%)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
            }}
          >
            {isEnabled
              ? toggleOnIcon || (
                // 🔁 fallback icon (if prop not passed)
                <svg viewBox="0 0 40 24" width="35" height="24">
                  <rect x="0" y="0" width="40" height="24" rx="12" fill="#7F56DA" />
                  <circle cx="28" cy="12" r="10" fill="white" />
                </svg>
              )
              : toggleOffIcon || (
                <svg viewBox="0 0 40 24" width="35" height="24">
                  <rect x="0" y="0" width="40" height="24" rx="12" fill="#bbb" />
                  <circle cx="12" cy="12" r="10" fill="white" />
                </svg>
              )}
          </div>
        )}


      </div>

    );
  }
);

export default SearchingBar;
